import { useRouter } from '@tanstack/react-router';
import { ConnectorRouteView, JourneysConnectorHandoff, useConnectorHandoffHrefs } from '@/features/integrations';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import SegmentOutPage from './index.page';

/** The two spellings of this connector's URL. */
function useHandoffHrefs() {
  const router = useRouter();

  return useConnectorHandoffHrefs({
    buildMigrated: (workspaceId) =>
      router.buildLocation({ to: '/workspaces/$workspaceId/integrations/segment_out', params: { workspaceId } }).href,
    buildLegacy: (workspaceId) =>
      router.buildLocation({
        to: '/workspaces/$workspaceId/journeys/integrations/segment_out',
        params: { workspaceId },
      }).href,
  });
}

/**
 * The old `/journeys/integrations/segment_out` URL.
 *
 * Answers for the flag alone, as every other connector's twin does. This URL used
 * to read the workspace's record as well, because a record predating the migrated
 * form was served by the embedded app and had to stay here (FPS-3689) — so the
 * migrated page handed those back and this URL declined to forward them.
 *
 * Both generations now have a form on the migrated page, so there is nothing to
 * hand back. Removing the read also removes a hazard that outlived its purpose:
 * rendering the embedded app at a URL Hydra claims meant Ember was told the route
 * had changed and then refused to move, because its own guard asks
 * `window.__hydra__.isHydraRoute` first and this URL answers yes. A reader
 * arriving from another Ember page therefore kept the page they were already on,
 * under this URL. Forwarding unconditionally is what every other twin does, and it
 * never leaves Ember holding a URL Hydra owns.
 */
export function JourneysSegmentOutHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.SegmentCdpIntegrationsMigration);
  const { migrated } = useHandoffHrefs();

  return <JourneysConnectorHandoff isMigrated={isMigrated} migratedHref={migrated} />;
}

/**
 * The migrated `/integrations/segment_out` URL.
 *
 * `ConnectorRouteView` applies the gates Ember's `integrations` root route applied.
 * This page's own route added no further gate — the Segment permissions gate the
 * controls, not the page — so nothing nests inside it here.
 */
export function SegmentOutRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.SegmentCdpIntegrationsMigration);
  const { legacy } = useHandoffHrefs();

  return (
    <ConnectorRouteView isMigrated={isMigrated} legacyHref={legacy}>
      {(workspaceId) => <SegmentOutPage workspaceId={workspaceId} />}
    </ConnectorRouteView>
  );
}
